import POSConfigModule from "@/store/modules/pos-config";
import {
  ICashFloat,
  ICashTransfer,
  IManualClosingSummary,
  IDigitalClosingSummary,
} from "@/interfaces/cash-float/cash-float";
import request from "@/utils/request";
import { SharedCallbacks } from "@/utils/shared-resources";

export async function handleCashFloat(cashFloats?: ICashFloat[]) {
  const params: { [posConfigId: string]: number | undefined } = {};

  const getPOSConfigId = SharedCallbacks.getPOSConfigId;
  if (getPOSConfigId) {
    params.posConfigId = getPOSConfigId();
  }

  const response = await request({
    method: "POST",
    url: `/group/${POSConfigModule.groupId}/synchronize-cash-float`,
    data: {
      cashFloats,
    },
    params,
  });

  return response.data;
}

/**
 * Push Synchronization for local cashfloat
 * @param cashFloats ICashfloat created locally
 */
// eslint-disable-next-line
export async function pushSyncCashfloat(
  cashFloats?: ICashFloat[]
): Promise<any> {
  const params: { [posConfigId: string]: number | undefined } = {};

  const getPOSConfigId = SharedCallbacks.getPOSConfigId;
  if (getPOSConfigId) {
    params.posConfigId = getPOSConfigId();
  }

  const response = await request({
    method: "POST",
    url: `/group/${POSConfigModule.groupId}/synchronize-cash-float`,
    data: {
      cashFloats,
    },
    params,
  });

  return response.data;
}

/**
 * Push Synchronization for local cash transfers
 * @param cashTransfer: ICashTransfer
 */
// eslint-disable-next-line
export async function pushSyncCashTransfer(
  cashTransfer: ICashTransfer
): Promise<any> {
  const {
    amount,
    currency,
    from,
    to,
    note,
    channel,
    uuid,
    isGainLoss,
    cfUid,
    createdAt,
  } = cashTransfer;
  const params: { [posConfigId: string]: number | undefined } = {};
  const getPOSConfigId = SharedCallbacks.getPOSConfigId;
  if (getPOSConfigId) {
    params.posConfigId = getPOSConfigId();
  }
  const response = await request({
    method: "POST",
    url: `/group/${POSConfigModule.groupId}/cash-mouvement`,
    data: {
      amount,
      currency,
      from,
      to,
      note,
      channel,
      uuid,
      isGainLoss,
      cfUid,
      createdAt,
    },
    params,
  });
  return response.data;
}

/**
 * V2 cash transfer — simplified body.
 * POST /en/group/{groupId}/cash-mouvement  { amount, currency, from, to, note }
 *
 * L3-8433: the simplification was meant to be of the BODY only. Without
 * posConfigId the backend cannot take its POS branch (user owns this config)
 * and falls through to the B2B one, which demands the group-level
 * POST_CASH_MOV_AND_DEVICE right — a cashier who lacks it is refused and the
 * closure transfer to the safe is lost. Even when it succeeds the movement is
 * stored with no config, so the cashbook cannot attribute it to the till.
 * Same four lines as every other call in this file.
 */
export async function pushSyncCashTransferV2(payload: {
  amount: number;
  currency: string;
  from: number | null;
  to: number | null;
  note: string;
  cfUid?: string | null;
}): Promise<any> {
  const params: { [posConfigId: string]: number | undefined } = {};

  const getPOSConfigId = SharedCallbacks.getPOSConfigId;
  if (getPOSConfigId) {
    params.posConfigId = getPOSConfigId();
  }

  const response = await request({
    method: "POST",
    url: `/group/${POSConfigModule.groupId}/cash-mouvement`,
    data: payload,
    params,
  });
  return response.data;
}

/**
 * Fetch the previous cashfloat for a given cash location (V2).
 * GET /en/group/{groupId}/cash-locations/{locationId}/previous-cashfloat
 */
export async function getPreviousCashfloatForLocation(
  locationId: number
): Promise<any> {
  const response = await request({
    method: "GET",
    url: `/group/${POSConfigModule.groupId}/cash-locations/${locationId}/previous-cashfloat`,
  });
  return response.data;
}

/**
 * Get the manual closing summary for closing a cash float (V1).
 * @param cashfloatUuid
 * @returns IManualClosingSummary
 */
export async function getManualClosingSummary(
  cashfloatUuid: string
): Promise<IManualClosingSummary> {
  const response = await request({
    method: "GET",
    url: `/group/${POSConfigModule.groupId}/cash-floats/${cashfloatUuid}/closing-summary/manual`,
  });

  return response.data.data;
}

/**
 * Get the manual closing summary for closing a cash float (V2).
 * GET /en/group/{groupId}/cash-floats/{uuid}/closing-summary/manual
 */
export async function getManualClosingSummaryV2(
  cashfloatUuid: string
): Promise<IManualClosingSummary> {
  const response = await request({
    method: "GET",
    url: `/group/${POSConfigModule.groupId}/cash-floats/${cashfloatUuid}/closing-summary/manual`,
  });

  return response.data.data;
}

/**
 * Get the digital closing summary for closing a cash float (V1).
 * @param cashfloatUuid The UUID of the cash float
 * @returns IDigitalClosingSummary
 */
export async function getDigitalClosingSummary(
  cashfloatUuid: string
): Promise<IDigitalClosingSummary> {
  const response = await request({
    method: "GET",
    url: `/group/${POSConfigModule.groupId}/cash-floats/${cashfloatUuid}/closing-summary/digital`,
  });

  return response.data.data;
}

/**
 * Get the digital closing summary for closing a cash float (V2).
 * GET /en/group/{groupId}/cash-floats/{uuid}/closing-summary/digital
 */
export async function getDigitalClosingSummaryV2(
  cashfloatUuid: string
): Promise<IDigitalClosingSummary> {
  const response = await request({
    method: "GET",
    url: `/group/${POSConfigModule.groupId}/cash-floats/${cashfloatUuid}/closing-summary/digital`,
  });

  return response.data.data;
}
